import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { clientAuthorizedGet, clientAuthorizedPatch } from 'utils/fetchUtils';
import { API_ROUTES } from 'utils/constants';
import {
  StorageProvider,
  StorageProviderType,
  UpdateStorageProviderRequest,
} from 'types/storageProvider';
import Button from 'components/Button';
import { PROVIDER_TYPES, SAMPLE_CREDENTIALS } from './storageProviderSamples';
import styles from './Storage.module.scss';

interface EditStorageProviderModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (config: StorageProvider) => void;
  onError: (message: string) => void;
  providerId: string;
}

function EditStorageProviderModal({
  show,
  onHide,
  onSuccess,
  onError,
  providerId,
}: EditStorageProviderModalProps) {
  const [providerType, setProviderType] = useState<StorageProviderType>('s3');
  const [credentialsJsonStr, setCredentialsJsonStr] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadProvider = useCallback(async () => {
    if (!providerId) return;
    setIsLoading(true);
    try {
      const config = await clientAuthorizedGet<StorageProvider>(
        `${API_ROUTES.STORAGE_PROVIDERS}/${providerId}`,
        undefined,
        undefined,
      );
      setProviderType(config.providerType);
      setCredentialsJsonStr(SAMPLE_CREDENTIALS[config.providerType]);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load provider.';
      onError(msg);
      onHide();
    } finally {
      setIsLoading(false);
    }
  }, [providerId, onError, onHide]);

  const handleProviderTypeChange = useCallback(
    (newType: StorageProviderType) => {
      setProviderType(newType);
      setCredentialsJsonStr(SAMPLE_CREDENTIALS[newType]);
    },
    [],
  );

  useEffect(() => {
    if (show && providerId) {
      void loadProvider();
    }
  }, [show, providerId, loadProvider]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const body: UpdateStorageProviderRequest = {};
      const trimmed = credentialsJsonStr.trim();
      const isUnchangedSample =
        trimmed === SAMPLE_CREDENTIALS[providerType].trim();
      if (trimmed && !isUnchangedSample) {
        try {
          body.credentialsJson = JSON.parse(credentialsJsonStr) as Record<
            string,
            unknown
          >;
        } catch {
          onError('Invalid JSON in credentials.');
          return;
        }
      }
      body.providerType = providerType;
      setIsSubmitting(true);
      try {
        const updated = await clientAuthorizedPatch<StorageProvider>(
          `${API_ROUTES.STORAGE_PROVIDERS}/${providerId}`,
          body as unknown as Record<string, unknown>,
          undefined,
        );
        onSuccess(updated);
        onHide();
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Failed to update storage provider.';
        onError(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [providerId, providerType, credentialsJsonStr, onSuccess, onError, onHide],
  );

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Edit storage provider</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(e) => void handleSubmit(e)}>
        <Modal.Body>
          {isLoading ? (
            <p className={styles.loadingText}>Loading…</p>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Provider type</Form.Label>
                <Form.Select
                  value={providerType}
                  onChange={(e) =>
                    handleProviderTypeChange(
                      e.target.value as StorageProviderType,
                    )
                  }
                  aria-label="Provider type"
                >
                  {PROVIDER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Credentials (JSON, optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={credentialsJsonStr}
                  onChange={(e) => setCredentialsJsonStr(e.target.value)}
                  className={styles.credentialsInput}
                  aria-label="Credentials JSON"
                />
                <Form.Text className="text-muted">
                  Replace the placeholder with your new credentials to update,
                  or clear the box and save to keep existing credentials.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || isLoading}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default EditStorageProviderModal;
